// v11.0.0 — Calendar → Distribution link.
//
// Reads the Bookings Redux slice + Packages + DCP jobs, computes
// "screenings booked in the next 7 days where the booked DCP doesn't
// yet have a successful Share to the venue", and renders a single
// yellow alert at the top of the Distribution page.
//
// Click a row → opens the Distribute dialog pre-targeted at that DCP.
// Hides itself when there's nothing to act on.
//
// Pure helper `computeUpcomingNeeds()` is exported so tests can pin
// the logic without rendering the alert.
import React from 'react';
import {
  Alert, AlertTitle, Box, Chip, Button, Typography, Stack,
  // Chip is still used above for the "Today" / "in N days" pill;
  // the redundant "No DCP linked" chip was removed in this same
  // commit so don't drop Chip from the import.
} from '@mui/material';
import { CalendarToday as CalendarIcon } from '@mui/icons-material';

// Pure: given the four slices, return an array of
//   { booking, dcpJob, daysUntil, deliveredOk }
// for every booking whose first_screening lies in [now, now + 7d]
// AND whose DCP+venue combo has no active, non-revoked successful Share.
//
// `now` defaults to `new Date()`; tests override for determinism.
export function computeUpcomingNeeds({
  bookings = [],
  kdmRecords = [],
  packages = [],
  dcpJobs = [],
  now = new Date(),
}) {
  const horizon = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

  // Build a venue → set of dcp_job_ids that have been successfully
  // delivered. We treat "successful delivery" as: there exists a
  // KDM record for the (dcp_job, screen-of-venue) pair AND the share
  // attached to its package is not revoked. The package shape doesn't
  // carry KDMs but kdm_records has venue_id directly.
  const deliveredCombos = new Set();
  for (const k of kdmRecords) {
    if (k.deleted_at) continue;
    if (k.status !== 'completed') continue;
    if (k.dcp_job_id && k.venue_id) {
      deliveredCombos.add(`${k.dcp_job_id}:${k.venue_id}`);
    }
  }

  // Also any Share with email_sent_at + not revoked counts as
  // "delivered to that recipient" — though Shares don't carry venue_id
  // directly; we infer via the package.source_job_id matching
  // dcp_job_id, and look up venue by matching recipient_email against
  // any KDM record's tech_contact_email. (Imprecise, but it covers
  // the unencrypted-DCP case where there's no KDM at all.)
  // Skipped for now — covered by the KDM-completed path which is the
  // canonical signal for encrypted DCPs (and the only kind blocked by
  // missing certs).

  const out = [];
  for (const b of bookings) {
    if (!b.first_screening) continue;
    const first = new Date(b.first_screening);
    if (Number.isNaN(first.getTime())) continue;
    if (first < now || first > horizon) continue;
    const venueId = b.venue_id;
    const dcpId = b.dcp_job_id;
    const deliveredOk = dcpId && venueId
      ? deliveredCombos.has(`${dcpId}:${venueId}`)
      : false;
    const daysUntil = Math.ceil((first.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
    const dcpJob = dcpId ? dcpJobs.find((j) => j.id === dcpId) : null;
    out.push({ booking: b, dcpJob, daysUntil, deliveredOk });
  }
  // Sort by first_screening ascending — soonest first.
  out.sort((a, b) =>
    new Date(a.booking.first_screening) - new Date(b.booking.first_screening),
  );
  return out;
}


export default function UpcomingScreeningsAlert({
  bookings, kdmRecords, packages, dcpJobs, onJumpToDcp,
}) {
  const needs = React.useMemo(
    () => computeUpcomingNeeds({ bookings, kdmRecords, packages, dcpJobs }),
    [bookings, kdmRecords, packages, dcpJobs],
  );

  const undelivered = needs.filter((n) => !n.deliveredOk);
  if (undelivered.length === 0) {
    // Either no upcoming bookings, or all are already delivered. The
    // pipeline progress strip already covers the "all clear" signal —
    // we don't render an empty card here.
    return null;
  }

  return (
    <Alert
      severity="warning"
      data-testid="upcoming-screenings-alert"
      icon={<CalendarIcon />}
      sx={{ mb: 2, alignItems: 'flex-start' }}
    >
      <AlertTitle sx={{ fontWeight: 700 }}>
        {undelivered.length} upcoming screening{undelivered.length === 1 ? '' : 's'} not yet delivered (next 7 days)
      </AlertTitle>
      <Stack spacing={0.75} sx={{ mt: 1 }}>
        {undelivered.map((n) => {
          const b = n.booking;
          const venueLabel = b.venue_name
            || (dcpJobs.find((j) => j.id === b.dcp_job_id)?.name) // fall-back if venue label missing
            || 'Unknown venue';
          const dcpLabel = n.dcpJob ? n.dcpJob.name : (b.dcp_job_id ? `DCP #${b.dcp_job_id}` : 'No DCP linked yet');
          const screeningDate = new Date(b.first_screening).toLocaleDateString(undefined, {
            weekday: 'short', month: 'short', day: 'numeric',
          });
          return (
            <Box
              key={b.id}
              data-testid="upcoming-screenings-row"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
            >
              <Chip
                size="small"
                label={n.daysUntil === 0 ? 'Today' : (n.daysUntil === 1 ? 'Tomorrow' : `in ${n.daysUntil} days`)}
                color={n.daysUntil <= 2 ? 'error' : 'warning'}
                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
              />
              <Typography variant="caption" fontWeight={600}>
                {venueLabel}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                · {screeningDate}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
                · {dcpLabel}
              </Typography>
              {n.dcpJob && onJumpToDcp && (
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  onClick={() => onJumpToDcp(n.dcpJob.id)}
                  sx={{ py: 0.25, fontSize: '0.7rem' }}
                >
                  Distribute now
                </Button>
              )}
              {/* "No DCP linked yet" is already conveyed by the dcpLabel
                  typography above when b.dcp_job_id is null — no chip
                  needed (one fact, one place). */}
            </Box>
          );
        })}
      </Stack>
    </Alert>
  );
}
